/**
 * Deletes the character at the given position in the query statement string.
 * Used to set the cursor position back by one character when running a query
 * with enter key.
 * @param statement
 * @param position
 */
export function deleteCharAtPosition(
  statement: string,
  position: { lineNumber: number; column: number }
): { newString: string; newPosition: { lineNumber: number; column: number } } {
  // Split the statement by line breaks to work with individual lines
  const lines = statement.split('\n');

  // Validate the position
  if (position.lineNumber < 1 || position.lineNumber > lines.length) {
    throw new Error('Invalid line number');
  }

  const line = lines[position.lineNumber - 1];

  if (position.column < 1 || position.column > line.length + 1) {
    throw new Error('Invalid column number');
  }

  // Convert the position to an index in the original string
  let charIndex = 0;
  for (let i = 0; i < position.lineNumber - 1; i++) {
    charIndex += lines[i].length + 1; // Add 1 for the newline character
  }
  charIndex += position.column - 1; // Column is 1-based

  // Now we have the character index, delete the previous character
  if (charIndex === 0) {
    // If we're at the start of the string, do nothing
    return { newString: statement, newPosition: position };
  }

  let newLineNumber = position.lineNumber;
  let newColumn = position.column;

  // Check if we're deleting a newline
  if (statement[charIndex - 1] === '\n') {
    // If we're deleting a newline, move to the end of the previous line
    newLineNumber -= 1;
    newColumn = lines[newLineNumber - 1].length + 1; // Move to the end of the previous line
  } else {
    // Otherwise, just move the cursor left by one character
    newColumn -= 1;
  }

  // Create the new string with the character deleted
  const newString = statement.slice(0, charIndex - 1) + statement.slice(charIndex);

  return {
    newString,
    newPosition: { lineNumber: newLineNumber, column: newColumn },
  };
}
